import styled from "@emotion/styled"
import { PFieldset, PInlineNotification } from "@porsche-design-system/components-react"
import { spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import { stockStatusMessages } from "@slfinrtl/ui-bff-routes"
import { useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { FormLayout, FormLayoutGroup } from "src/components/form"
import { DatepickerField } from "src/components/form/datepicker/DatepickerField"
import { InputField } from "src/components/form/input/InputField"
import { RadioGroupField } from "src/components/form/radio/radioGroup/RadioGroupField"
import { SelectField } from "src/components/form/select/SelectField"
import { inventoryAndIntakeFieldMessages } from "../common/inventoryAndIntakeField.messages"
import type { InventoryAndIntakeFields } from "../inventoryAndIntake.fields"

const messages = defineMessages({
	customerVehicleRelationshipWarningMessage: {
		defaultMessage:
			"Setting the status to {inStock} will terminate the customer-vehicle relationship and deactivate the associated online services.\nIf the vehicle is still being used by the customer, select {incoming} instead.",
		id: "inventoryAndIntakeForm.customerVehicleRelationshipWarning.message",
	},
	customerVehicleRelationshipWarningTitle: {
		defaultMessage: "Customer-vehicle relationship will be terminated.",
		id: "inventoryAndIntakeForm.customerVehicleRelationshipWarning.title",
	},
})

interface InventoryAndIntakeFormProps {
	customerVehicleRelationshipWarningVisible: boolean
	fields: InventoryAndIntakeFields
}

export const InventoryAndIntakeForm = ({
	customerVehicleRelationshipWarningVisible,
	fields,
}: InventoryAndIntakeFormProps) => {
	const { formatMessage } = useIntl()
	const [customerVehicleRelationshipDismissed, setCustomerVehicleRelationshipDismissed] = useState(false)

	const dismissCustomerVehicleRelationship = () => {
		setCustomerVehicleRelationshipDismissed(true)
	}

	return (
		<FormLayoutGroup>
			<PFieldset label={formatMessage(inventoryAndIntakeFieldMessages.inventoryTitle)}>
				<FormLayout $width="quarter">
					<RadioGroupField {...fields.vehicleType} />
					<RadioGroupField {...fields.stockStatus} />
				</FormLayout>
				{customerVehicleRelationshipWarningVisible && !customerVehicleRelationshipDismissed && (
					<FormLayout $width="half">
						<CustomerVehicleRelationshipWarning
							state="warning"
							onDismiss={dismissCustomerVehicleRelationship}
							heading={formatMessage(messages.customerVehicleRelationshipWarningTitle)}
						>
							<FormattedMessage
								{...messages.customerVehicleRelationshipWarningMessage}
								values={{
									incoming: formatMessage(stockStatusMessages.INCOMING),
									inStock: formatMessage(stockStatusMessages.IN_STOCK),
								}}
							/>
						</CustomerVehicleRelationshipWarning>
					</FormLayout>
				)}
			</PFieldset>

			<PFieldset label={formatMessage(inventoryAndIntakeFieldMessages.intakeTitle)}>
				<FormLayout $width="quarter">
					{fields.expectedArrivalDate && <DatepickerField {...fields.expectedArrivalDate} />}
					<DatepickerField {...fields.arrivalDate} />
					{fields.availableFromDate && <DatepickerField {...fields.availableFromDate} />}
					<DatepickerField {...fields.availableForCustomerFromDate} />
					<SelectField {...fields.vehicleOrigin} />
					<InputField {...fields.inventoryNumber} />
					<InputField {...fields.typeKeyNumber} />
					{fields.masterNumberCh && <InputField {...fields.masterNumberCh} />}
					<InputField {...fields.licensePlate} />
					<InputField {...fields.mileageAccordingToPreviousOwner} />
				</FormLayout>
			</PFieldset>
		</FormLayoutGroup>
	)
}

const CustomerVehicleRelationshipWarning = styled(PInlineNotification)`
	margin-top: ${spacingStaticMd};
`
